import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import PageState from "../../components/common/PageState";
import { useGlobal } from "../../contexts/GlobalContext";
import { useCurrentProject } from "../../hooks/useCurrentProject";
import request from "../../utils/request";
import {
  buildToolQuotaCards,
  classifyUsageFailure,
  type ModelUsageItem,
  type ProjectUsageOverview,
  type UsageFailure,
  quotaDisplay,
  quotaPercent,
} from "./projectUsageModel";

const { Title, Text } = Typography;

const formatNumber = (value: number) => value.toLocaleString("zh-CN");
const formatMoney = (value: number) => `¥${value.toFixed(4)}`;
const formatReset = (value?: string) =>
  value ? new Date(value).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

const modelColumns: ColumnsType<ModelUsageItem> = [
  { title: "模型", dataIndex: "modelName", key: "modelName", ellipsis: true },
  { title: "调用", dataIndex: "requestCount", key: "requestCount", align: "right", render: formatNumber },
  { title: "输入 Token", dataIndex: "promptTokens", key: "promptTokens", align: "right", render: formatNumber },
  { title: "输出 Token", dataIndex: "completionTokens", key: "completionTokens", align: "right", render: formatNumber },
  { title: "实际 Token", dataIndex: "totalTokens", key: "totalTokens", align: "right", render: formatNumber },
  { title: "模型成本", dataIndex: "totalCost", key: "totalCost", align: "right", render: formatMoney },
];

const ProjectUsage: React.FC = () => {
  const navigate = useNavigate();
  const { API_BASE_URL } = useGlobal();
  const { project } = useCurrentProject();
  const projectID = project?.id;
  const [data, setData] = useState<ProjectUsageOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<UsageFailure | null>(null);

  const loadUsage = useCallback(async (signal?: AbortSignal) => {
    if (!projectID) return;
    setLoading(true);
    setFailure(null);
    try {
      const response = await request.get<{ code: number; msg: string; data: ProjectUsageOverview }>(
        `${API_BASE_URL}/api/v3/user/project-usage`,
        { params: { project_id: projectID }, signal }
      );
      if (response.data.code !== 200) {
        setFailure(classifyUsageFailure(undefined, { msg: response.data.msg }));
        return;
      }
      setData(response.data.data);
    } catch (requestError: any) {
      if (requestError?.code === "ERR_CANCELED") return;
      const response = requestError?.response;
      setFailure(classifyUsageFailure(
        response?.status,
        response?.data || { message: requestError?.message },
        response?.headers?.["x-trace-id"]
      ));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_BASE_URL, projectID]);

  useEffect(() => {
    const controller = new AbortController();
    loadUsage(controller.signal);
    return () => controller.abort();
  }, [loadUsage]);

  const quotaCards = useMemo(() => buildToolQuotaCards(data?.toolQuotas), [data?.toolQuotas]);

  if (!project || !projectID) return null;

  return (
    <main style={{ padding: 24, maxWidth: 1180, margin: "0 auto" }} aria-labelledby="project-usage-title">
      <Space direction="vertical" size={4} style={{ marginBottom: 20 }}>
        <Title id="project-usage-title" level={3} style={{ margin: 0 }}>用量</Title>
        <Text type="secondary">{project.name} · 本月项目用量与账户工具额度</Text>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
        message="三套用量独立计量"
        description="摘要继续扣除旧 Token 账本；Agent、工作流和 MCP 工具使用会员日/周额度；模型成本记录实际输入输出 Token 与上游成本，不会在浏览器中推算额度。"
      />

      {loading && !data ? (
        <PageState kind="loading" title="正在读取服务端用量" />
      ) : failure && !data ? (
        <PageState
          kind={failure.kind}
          description={failure.message}
          traceId={failure.traceId}
          actionLabel={failure.kind === "plan-limit" ? "查看套餐" : failure.kind === "forbidden" ? "返回项目" : "重试"}
          onAction={failure.kind === "plan-limit" ? () => navigate("/payment-checkout?type=member") : failure.kind === "forbidden" ? () => navigate("/") : () => loadUsage()}
        />
      ) : data ? (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <section aria-labelledby="summary-usage-title">
            <Card title={<span id="summary-usage-title">摘要 Token</span>} extra={<Tag>项目级 · 本月</Tag>}>
              <Row gutter={[16, 16]}>
                <Col xs={12} md={6}><Statistic title="旧账本已扣" value={data.summary.billedTokens} formatter={(value) => formatNumber(Number(value))} /></Col>
                <Col xs={12} md={6}><Statistic title="实际模型 Token" value={data.summary.actualTokens} formatter={(value) => formatNumber(Number(value))} /></Col>
                <Col xs={12} md={6}><Statistic title="摘要请求" value={data.summary.requestCount} formatter={(value) => formatNumber(Number(value))} /></Col>
                <Col xs={12} md={6}><Statistic title="当前 Token 余额" value={project.token ?? 0} formatter={(value) => formatNumber(Number(value))} /></Col>
              </Row>
            </Card>
          </section>

          <section aria-labelledby="tool-usage-title">
            <Card
              title={<Space><span id="tool-usage-title">工具日/周额度</span><Tag color={data.toolFrozen ? "red" : "blue"}>{data.toolPlan || "free"}</Tag></Space>}
              extra={<Tag>账户级</Tag>}
            >
              {data.toolFrozen && <Alert type="warning" showIcon message="账户已冻结，工具调用暂不可用" style={{ marginBottom: 16 }} />}
              {quotaCards.length === 0 ? <PageState kind="empty" compact title="暂无工具额度" description="当前计划未返回可展示的日/周工具额度。" /> : (
                <Row gutter={[16, 16]}>
                  {quotaCards.map((quota) => (
                    <Col xs={24} md={8} key={quota.scenario}>
                      <Card size="small" title={quota.label}>
                        <Space direction="vertical" size={8} style={{ width: "100%" }}>
                          <Text strong>{quotaDisplay(quota)}</Text>
                          <Progress percent={quotaPercent(quota)} showInfo={quota.limit > 0} status={quota.remaining === 0 && quota.limit > 0 ? "exception" : "normal"} />
                          <Text type="secondary">重置时间：{formatReset(quota.resetAt)}</Text>
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card>
          </section>

          <section aria-labelledby="model-usage-title">
            <Card title={<span id="model-usage-title">模型成本</span>} extra={<Tag>项目级 · 本月</Tag>}>
              <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={12} md={8}><Statistic title="模型调用" value={data.modelTotals.requestCount} formatter={(value) => formatNumber(Number(value))} /></Col>
                <Col xs={12} md={8}><Statistic title="实际 Token" value={data.modelTotals.totalTokens} formatter={(value) => formatNumber(Number(value))} /></Col>
                <Col xs={24} md={8}><Statistic title="上游成本" value={data.modelTotals.totalCost} formatter={(value) => formatMoney(Number(value))} /></Col>
              </Row>
              <Table<ModelUsageItem>
                rowKey="modelName"
                columns={modelColumns}
                dataSource={data.models}
                pagination={false}
                size="small"
                scroll={{ x: 760 }}
                locale={{ emptyText: <PageState kind="empty" compact title="本月暂无模型调用" description="产生模型调用后，实际 Token 与成本会显示在这里。" /> }}
              />
            </Card>
          </section>

          {failure && <Alert type="warning" showIcon message="刷新失败，当前展示上一次成功的数据" description={failure.message} action={<Button onClick={() => loadUsage()}>重新刷新</Button>} />}
        </Space>
      ) : null}
    </main>
  );
};

export default ProjectUsage;
